import React, { useEffect } from 'react';
import Faq from '../components/Faq';
import { Monitor, ShoppingBag, Globe, ArrowRight, Code, Smartphone, Search, Zap, MessageSquare, Clock, Palette, Rocket, CheckCircle, Shield, BadgeCheck, PhoneCall } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const CreationSiteWeb: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = 'Création de Sites Web Professionnels | SOS IT Services';
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Création de sites web professionnels : site vitrine, e-commerce, sur-mesure. Design moderne, référencement SEO inclus. Devis gratuit et rapide par nos experts.');
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://sos-it-services.fr/creation-site-web');
    // OG tags
    const ogTags: Record<string, string> = {
      'og:title': 'Création de Sites Web Professionnels | SOS IT Services',
      'og:description': 'Site vitrine ou e-commerce sur-mesure, design moderne, SEO inclus. Devis gratuit par nos experts web.',
      'og:url': 'https://sos-it-services.fr/creation-site-web',
      'og:image': 'https://sos-it-services.fr/images/website-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    window.scrollTo(0, 0);

    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const revealUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  };

  const howItWorksSteps = [
    {
      id: '01',
      title: 'Décrivez votre projet',
      description: 'Formulaire en ligne ou WhatsApp : partagez votre vision, vos besoins et vos objectifs en quelques minutes.',
      icon: MessageSquare,
      delay: 0,
    },
    {
      id: '02',
      title: 'Devis clair sous 48h',
      description: 'Vous recevez une proposition détaillée avec planning, fonctionnalités et tarif fixe sans surprise.',
      icon: Clock,
      delay: 0.08,
    },
    {
      id: '03',
      title: 'Design & développement',
      description: 'On construit votre site avec des points d\'étape réguliers pour valider chaque avancée ensemble.',
      icon: Palette,
      delay: 0.16,
    },
    {
      id: '04',
      title: 'Mise en ligne',
      description: 'Votre site est livré clé en main, optimisé SEO, avec formation et support inclus.',
      icon: Rocket,
      delay: 0.24,
    },
  ] as const;

  const vitrineFeatures = [
    {
      title: 'Design sur mesure',
      description: 'Création graphique entièrement personnalisée selon votre charte et votre identité visuelle.',
      icon: Code,
      accent: 'from-[#5865F2]/20 to-transparent',
    },
    {
      title: 'Responsive design',
      description: 'Adaptation parfaite à tous les appareils : ordinateurs, tablettes et smartphones.',
      icon: Smartphone,
      accent: 'from-cyan-400/20 to-transparent',
    },
    {
      title: 'SEO optimisé',
      description: 'Optimisation pour les moteurs de recherche afin d\'améliorer votre visibilité en ligne.',
      icon: Search,
      accent: 'from-emerald-400/20 to-transparent',
    },
  ] as const;

  const ecommerceFeatures = [
    {
      title: 'Gestion de produits',
      description: 'Interface d\'administration intuitive pour gérer vos produits, stocks et catégories.',
      icon: ShoppingBag,
      accent: 'from-[#5865F2]/20 to-transparent',
    },
    {
      title: 'Paiement sécurisé',
      description: 'Intégration de solutions de paiement sécurisées (Stripe, PayPal, CB) conformes aux normes.',
      icon: Zap,
      accent: 'from-cyan-400/20 to-transparent',
    },
    {
      title: 'Marketing intégré',
      description: 'Outils marketing intégrés pour promotions, codes promo et fidélisation client.',
      icon: Globe,
      accent: 'from-emerald-400/20 to-transparent',
    },
  ] as const;

  const webNeeds = ['Site vitrine', 'E-commerce', 'Landing page', 'Refonte', 'SEO', 'Maintenance'] as const;

  return (
    <div className="pt-24 md:pt-32">

      {/* Hero */}
      <div className="relative py-24 min-h-[520px] flex items-center overflow-hidden bg-[#2F3136]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/website-hero.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover" style={{ objectPosition: 'center 58%' }}
          />
          <div className="absolute inset-0 bg-[#2F3136]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <motion.p
              className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B9C4FF] mb-4"
              animate={{ boxShadow: ['0 0 0 rgba(88,101,242,0)', '0 0 24px rgba(88,101,242,0.25)', '0 0 0 rgba(88,101,242,0)'] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            >
              Devis gratuit sous 48h
            </motion.p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Votre site web <span className="text-[#5865F2]">professionnel clé en main</span>
            </h1>
            <p className="text-gray-200 text-base md:text-lg max-w-3xl mx-auto mb-8">
              Site vitrine ou e-commerce : décrivez votre projet et recevez une proposition claire avec tarif fixe, planning précis et zéro surprise.
            </p>

            {/* Badges réassurance */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3 mb-10"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
            >
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Zap className="h-4 w-4 text-[#5865F2]" /> Livraison en 2-4 semaines
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Search className="h-4 w-4 text-[#5865F2]" /> SEO inclus
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Shield className="h-4 w-4 text-[#5865F2]" /> Tarif fixe, sans surprise
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <BadgeCheck className="h-4 w-4 text-[#5865F2]" /> Support & formation inclus
              </span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.16, ease: 'easeOut' }}
            >
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
                >
                  Demander un devis gratuit <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
              <motion.a
                href="#web-vitrine"
                className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Voir les forfaits
              </motion.a>
            </motion.div>

          </motion.div>
        </div>
      </div>

      {/* Comment ça marche */}
      <section className="relative py-20 bg-[#36393F] overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -top-24 left-[10%] h-56 w-56 rounded-full bg-[#5865F2]/20 blur-3xl"
            animate={{ x: [0, 20, -8, 0], y: [0, -12, 14, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-24 right-[6%] h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl"
            animate={{ x: [0, -24, 10, 0], y: [0, 12, -18, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/35 bg-[#5865F2]/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-[#9BA8FF] uppercase mb-4">
              Notre méthode
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Comment ça <span className="text-[#5865F2]">marche ?</span>
            </h2>
            <p className="text-gray-400 text-sm">De l'idée à la mise en ligne en 4 étapes simples</p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[#5865F2]/35 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {howItWorksSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="relative">
                    {index < howItWorksSteps.length - 1 && (
                      <div className="md:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-[#5865F2]/40 to-transparent z-10" />
                    )}
                  <motion.div
                    className="group relative text-center p-6 md:p-7 rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/[0.03] backdrop-blur-sm"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.55, delay: step.delay, ease: 'easeOut' }}
                    whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.22, ease: 'easeOut' } }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#5865F2]/20 via-transparent to-cyan-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      aria-hidden="true"
                    />

                    <div className="relative z-10">
                      <motion.div
                        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5865F2]/20 border border-[#5865F2]/35 text-[#9BA8FF]"
                        whileHover={{ rotate: [0, -5, 5, 0] }}
                        transition={{ duration: 0.45 }}
                      >
                        <Icon className="h-6 w-6" />
                      </motion.div>

                      <span className="inline-flex rounded-full border border-[#5865F2]/30 bg-[#5865F2]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9BA8FF] mb-3">
                        Étape {step.id}
                      </span>

                      <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                      <p className="text-gray-300/90 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#5865F2]/25 to-transparent" />

      {/* Section Site Vitrine */}
      <section id="web-vitrine" className="py-16 bg-[#2F3136]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <Monitor className="h-6 w-6 text-[#5865F2] mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Site <span className="text-[#5865F2]">Vitrine</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-gray-300 max-w-3xl mb-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            Valorisez votre image et présentez votre activité avec un site vitrine professionnel, personnalisé et optimisé pour le référencement.
          </motion.p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            {vitrineFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="group relative overflow-hidden rounded-xl border border-white/12 bg-gradient-to-b from-white/10 to-white/[0.03] p-5 cursor-pointer"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.01 }}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${feature.accent} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/10 text-[#B9C4FF]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Forfaits vitrine */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Essentiel</h3>
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-wide text-gray-200">
                  Idéal pour démarrer
                </span>
              </div>
              <div className="rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 mb-4">
                <p className="text-[#AEB9FF] text-xs uppercase tracking-wide mb-2">À partir de</p>
                <p className="text-[#D2D9FF] font-semibold text-lg">990€</p>
              </div>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Design personnalisé</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Jusqu'à 5 pages</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Responsive design</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Optimisation SEO basique</li>
              </ul>
            </motion.div>
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Premium</h3>
                <span className="rounded-full border border-[#5865F2]/30 bg-[#5865F2]/10 px-2.5 py-1 text-[11px] uppercase tracking-wide text-[#9BA8FF]">
                  Le plus choisi
                </span>
              </div>
              <div className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 p-4 mb-4">
                <p className="text-cyan-200 text-xs uppercase tracking-wide mb-2">À partir de</p>
                <p className="text-white font-semibold text-lg">1490€</p>
              </div>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Design sur mesure exclusif</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Jusqu'à 10 pages</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Animations et interactions</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> SEO avancé</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Maintenance 1 an incluse</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />

      {/* Section E-commerce */}
      <section id="web-ecommerce" className="py-16 bg-[#36393F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <ShoppingBag className="h-6 w-6 text-[#5865F2] mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Site <span className="text-[#5865F2]">E-commerce</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-gray-300 max-w-3xl mb-6"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            Vendez vos produits en ligne avec une boutique e-commerce performante, sécurisée et facile à administrer.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-2 mb-8"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }}
          >
            {webNeeds.map((need) => (
              <span key={need} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-gray-200">
                {need}
              </span>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            {ecommerceFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="group relative overflow-hidden rounded-xl border border-white/12 bg-gradient-to-b from-white/10 to-white/[0.03] p-5 cursor-pointer"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.01 }}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${feature.accent} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/10 text-[#B9C4FF]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Forfaits e-commerce */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Standard</h3>
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-wide text-gray-200">
                  Pour démarrer
                </span>
              </div>
              <div className="rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 mb-4">
                <p className="text-[#AEB9FF] text-xs uppercase tracking-wide mb-2">À partir de</p>
                <p className="text-[#D2D9FF] font-semibold text-lg">2490€</p>
              </div>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Jusqu'à 100 produits</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Paiement CB, PayPal</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Gestion des stocks</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Responsive design</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5865F2] flex-shrink-0" /> Formation de base</li>
              </ul>
            </motion.div>
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Business</h3>
                <span className="rounded-full border border-[#5865F2]/30 bg-[#5865F2]/10 px-2.5 py-1 text-[11px] uppercase tracking-wide text-[#9BA8FF]">
                  Le plus choisi
                </span>
              </div>
              <div className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 p-4 mb-4">
                <p className="text-cyan-200 text-xs uppercase tracking-wide mb-2">À partir de</p>
                <p className="text-white font-semibold text-lg">3990€</p>
              </div>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Produits illimités</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Multi-devises, multi-langues</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Intégration API (transporteurs, ERP)</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Marketing avancé (abandon panier, etc.)</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" /> Maintenance et support premium</li>
              </ul>
            </motion.div>
          </div>

          {/* CTA pro inline */}
          <motion.div
            className="bg-white/5 rounded-xl p-6 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div>
              <p className="text-white font-semibold mb-1">Un projet sur mesure ?</p>
              <p className="text-gray-300 text-sm">Discutons de vos besoins spécifiques : fonctionnalités avancées, intégrations, volume de produits.</p>
            </div>
            <div className="flex shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-5 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300 whitespace-nowrap"
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                Parlons de votre projet <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Preuve sociale */}
      <section className="py-16 bg-[#2F3136]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/35 bg-[#5865F2]/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-[#9BA8FF] uppercase mb-4">
              Références
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Ils nous ont fait <span className="text-[#5865F2]">confiance</span>
            </h2>
            <p className="text-gray-400 text-sm">Des projets web livrés avec succès</p>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-start justify-center gap-14"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          >
            <a
              href="https://aubergefruitiereperon.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 transition-opacity hover:opacity-100 opacity-70"
            >
              <div className="h-28 w-28 rounded-2xl bg-white/10 border border-white/15 p-4 flex items-center justify-center group-hover:border-[#5865F2]/40 transition-colors">
                <img
                  src="/images/logo-auberge.png"
                  alt="Auberge Fruitière de Péron"
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="text-gray-200 text-sm font-semibold group-hover:text-white transition-colors text-center">Auberge Fruitière de Péron</span>
              <span className="text-sm text-gray-300 text-center max-w-[230px] leading-relaxed">
                Création du site vitrine et mise en valeur de l'établissement.
              </span>
            </a>
            <a
              href="https://avis-clients.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 transition-opacity hover:opacity-100 opacity-70"
            >
              <div className="h-28 w-28 rounded-2xl bg-white/10 border border-white/15 p-4 flex items-center justify-center group-hover:border-[#5865F2]/40 transition-colors">
                <img
                  src="/images/avis-client-ai.png"
                  alt="Avis Clients AI"
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="text-gray-200 text-sm font-semibold group-hover:text-white transition-colors text-center">Avis Clients AI</span>
              <span className="text-sm text-gray-300 text-center max-w-[230px] leading-relaxed">
                Développement de la plateforme SaaS et de l'interface utilisateur.
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      <Faq showLink={false} showAllQuestions={false} />

      {/* CTA final */}
      <section className="py-14 bg-[#36393F] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="rounded-2xl border border-[#5865F2]/35 bg-gradient-to-r from-[#5865F2]/20 via-[#5865F2]/10 to-cyan-400/10 p-7 md:p-8 text-center"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="text-[#C7D0FF] text-xs uppercase tracking-wider mb-3">Prêt à lancer votre projet ?</p>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
              Décrivez votre projet en 2 minutes, devis gratuit sous 48h
            </h3>
            <p className="text-gray-200 text-sm md:text-base mb-6">
              Site vitrine ou e-commerce, on vous accompagne de A à Z avec un tarif fixe et un planning clair.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
            >
              Demander un devis gratuit <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default CreationSiteWeb;
